import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {BlogCategory, BlogIndexPage, BlogPage, NewsletterSubscription, NewsletterConfirmData, NewsletterCancelData} from './models';
import {map} from 'rxjs/operators';
import {RtPlatformService} from "../../rt-platform/rt-platform.service";

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  private ROOT = 'blog';
  constructor(
    private http: HttpClient,
    private platform: RtPlatformService,
  ) { }

  private getRequestUrl(pageUrl: string): string {
    return `${this.platform.baseUrl}/api/wagtail/pages/detail_by_path/?html_path=${pageUrl}`;
  }

  private getListUrl(group, value): string {
    return `${this.platform.baseUrl}/api/blog/${group}/${value}/`;
  }

  private getDetailUrl(slug): string {
    return `${this.platform.baseUrl}/api/blog/post/${slug}/`;
  }

  private getCategoryDetailUrl(name): string {
    return `${this.platform.baseUrl}/api/blog/category/detail/${name}/`;
  }

  loadIndex(): Observable<BlogIndexPage> {
    return this.http.get<BlogIndexPage>(this.getRequestUrl(this.ROOT)).pipe(
      map(blogIndex => ({
        ...blogIndex,
        items: blogIndex.items.map(item => new BlogPage().deserialize(item))
      }))
    );
  }

  loadEntry(slug): Observable<BlogPage> {
    return this.http.get<BlogPage>(this.getDetailUrl(slug)).pipe(
      map(entry => new BlogPage().deserialize(entry))
    );
  }

  loadList(name, value): Observable<BlogPage[]> {
    return this.http.get<BlogPage[]>(this.getListUrl(name, value)).pipe(
      map(
        pages => pages.map(
          item => new BlogPage().deserialize(item)
        )
      )
    )
  }

  loadCategory(name): Observable<BlogCategory> {
    return this.http.get<BlogCategory>(this.getCategoryDetailUrl(name));
  }

  getPageUrlFromSlug(slug: string): string {
    return `${this.ROOT}/${slug}`;
  }

  getPageUrl(entry: BlogPage): string {
    return this.getPageUrlFromSlug(entry.slug);
  }

  loadSubscription(): Observable<NewsletterSubscription> {
    return this.http.get<NewsletterSubscription[]>('/api/blog/subscription/').pipe(
      map(resp => {
        return resp[0] ? resp[0] : null;
      })
    );
  }

  getSubscriptionByID(subscriptionID): Observable<NewsletterSubscription> {
    return this.http.get<NewsletterSubscription>(`/api/blog/subscription/${subscriptionID}/`);
  }

  sendSubscription(): Observable<NewsletterSubscription> {
    return this.http.post<NewsletterSubscription>('/api/blog/subscription/', {});
  }

  updateSubscription(subscriptionData): Observable<NewsletterSubscription> {
    return this.http.patch<NewsletterSubscription>(`/api/blog/subscription/${subscriptionData.id}/`, subscriptionData);
  }

  confirmSubscription(confirmationData: NewsletterConfirmData): Observable<NewsletterSubscription> {
    return this.http.patch<NewsletterSubscription>(`/api/blog/subscription/${confirmationData.id}/`, confirmationData);
  }

  cancelSubscription(cancellationData: NewsletterCancelData): Observable<NewsletterSubscription> {
    return this.http.patch<NewsletterSubscription>(`/api/blog/subscription/${cancellationData.id}/`, cancellationData);
  }
}
